import { useRef, useState } from "react";
import { Input } from "antd";

import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  PAM_MFA_ERROR_CODES,
  getPamMfaErrorCode,
  getPamMfaErrorMessage,
} from "../../../utils/auth/pamMfa";
import "./stylesAuthenticatorEnrollment.css";

function AuthenticatorChallenge({
  onVerify,
  onCancel,
  onSessionExpired,
}) {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);
  const submissionInFlight = useRef(false);

  const verifyCode = async (nextCode) => {
    if (
      nextCode.length !== 6 ||
      isRateLimited ||
      submissionInFlight.current
    ) {
      return;
    }

    submissionInFlight.current = true;
    setIsVerifying(true);
    setErrorMessage("");
    try {
      await onVerify(nextCode);
    } catch (error) {
      const errorCode = getPamMfaErrorCode(error);
      const message = getPamMfaErrorMessage(error);
      if (errorCode === PAM_MFA_ERROR_CODES.SESSION_EXPIRED) {
        onSessionExpired?.(message);
        return;
      }
      if (errorCode === PAM_MFA_ERROR_CODES.RATE_LIMITED) {
        setIsRateLimited(true);
      }
      setErrorMessage(message);
      window.setTimeout(() => inputRef.current?.focus?.(), 0);
    } finally {
      submissionInFlight.current = false;
      setIsVerifying(false);
    }
  };

  return (
    <form
      className="authenticator-enrollment authenticator-enrollment--challenge"
      aria-label={AppStrings.PAM_2FA_CHALLENGE_TITLE}
      onSubmit={(event) => {
        event.preventDefault();
        verifyCode(code);
      }}
    >
      <h1 className="authenticator-enrollment-title">
        {AppStrings.PAM_2FA_CHALLENGE_TITLE}
      </h1>
      <p className="authenticator-enrollment-description">
        {AppStrings.PAM_2FA_CHALLENGE_DESCRIPTION}
      </p>

      <label className="authenticator-enrollment-code-label">
        {AppStrings.PAM_2FA_CODE_LABEL}
      </label>
      <div className="authenticator-enrollment-code-container">
        <Input.OTP
          ref={inputRef}
          className="authenticator-enrollment-code"
          data-testid="totp-challenge-input"
          length={6}
          value={code}
          type="tel"
          inputMode="numeric"
          size="large"
          disabled={isVerifying || isRateLimited}
          onChange={(value) => {
            setCode(value.replace(/\D/g, ""));
            setErrorMessage("");
          }}
          onComplete={verifyCode}
          formatter={(value) => value.replace(/\D/g, "")}
        />
      </div>

      {errorMessage && (
        <p className="authenticator-enrollment-error" role="alert">
          {errorMessage}
        </p>
      )}

      <p className="authenticator-challenge-help">
        {AppStrings.PAM_2FA_LOST_DEVICE_HELP}
      </p>

      <button
        type="submit"
        className="loginButton authenticator-enrollment-submit"
        disabled={code.length !== 6 || isVerifying || isRateLimited}
      >
        <span className="loginButtonText">
          {isVerifying
            ? AppStrings.PAM_2FA_VERIFYING
            : AppStrings.PAM_2FA_VERIFY}
        </span>
      </button>
      <button
        type="button"
        className="authenticator-enrollment-cancel"
        onClick={onCancel}
        disabled={isVerifying}
      >
        {AppStrings.PAM_2FA_BACK_TO_LOGIN}
      </button>
    </form>
  );
}

export default AuthenticatorChallenge;
